import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";

/*
 * Copyright (C) 2017 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that they will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */
import React, { Component } from 'react';
import classnames from 'classnames';
import themeable from '@instructure/ui-themeable/lib';
import { bool, func, node, number, string, oneOf } from 'prop-types';
import IconArrowUpSolid from '@instructure/ui-icons/lib/Solid/IconArrowUp';
import IconArrowDownLine from '@instructure/ui-icons/lib/Line/IconArrowDown';
var styles = {
  componentId: 'feqxT',
  template: function template(theme) {
    return "\n\n.root_3o4v{-moz-appearance:none;-moz-user-select:none;-ms-user-select:none;-webkit-appearance:none;-webkit-user-select:none;appearance:none;background-color:".concat(theme.background || 'inherit', ";border:none;border-bottom-left-radius:").concat(theme.borderRadius || 'inherit', ";border-bottom-right-radius:").concat(theme.borderRadius || 'inherit', ";box-sizing:border-box;color:").concat(theme.color || 'inherit', ";cursor:pointer;display:block;font-family:").concat(theme.fontFamily || 'inherit', ";font-size:").concat(theme.fontSize || 'inherit', ";font-weight:").concat(theme.fontWeight || 'inherit', ";line-height:").concat(theme.lineHeight || 'inherit', ";outline:none;overflow:visible;padding:0;position:fixed;text-transform:").concat(theme.textTransform || 'inherit', ";touch-action:manipulation;transition:background-color 0.2s;user-select:none;white-space:nowrap}\n\n.root_3o4v:before{border:").concat(theme.focusRingWidth || 'inherit', " solid ").concat(theme.focusRingColor || 'inherit', ";border-bottom-left-radius:").concat(theme.borderRadius || 'inherit', ";border-bottom-right-radius:").concat(theme.borderRadius || 'inherit', ";box-sizing:border-box;content:\"\";height:calc(100% + 0.5rem);left:-0.25rem;opacity:0;position:absolute;top:-0.25rem;transform:scale(0.25);transition:all 0.2s ease-out;width:calc(100% + 0.5rem)}\n\n.root_3o4v:focus:before{opacity:1;transform:scale(1)}\n\n.root_3o4v:focus,.root_3o4v:hover{background-color:").concat(theme.backgroundHover || 'inherit', "}\n\n.root_3o4v:focus .icon_3o4v,.root_3o4v:hover .icon_3o4v{transform:translate3d(0,-0.0625rem,0) scale(1.2)}\n\n.root_3o4v[aria-disabled]{cursor:not-allowed;opacity:0.5;pointer-events:none}\n\n.icon_3o4v{-webkit-margin-start:").concat(theme.iconMargin || 'inherit', ";display:block;font-size:0.75rem;margin-inline-start:").concat(theme.iconMargin || 'inherit', ";transform:translate3d(0,-0.0625rem,0);transition:all 0.2s}\n\n[dir=ltr] .icon_3o4v{margin-left:").concat(theme.iconMargin || 'inherit', "}\n\n[dir=rtl] .icon_3o4v{margin-right:").concat(theme.iconMargin || 'inherit', "}\n\n.direction--down_3o4v .layout_3o4v,.direction--up_3o4v .layout_3o4v{-webkit-padding-end:").concat(theme.hasIconRightPadding || 'inherit', ";padding-inline-end:").concat(theme.hasIconRightPadding || 'inherit', "}\n\n[dir=ltr] .direction--down_3o4v .layout_3o4v,[dir=ltr] .direction--up_3o4v .layout_3o4v{padding-right:").concat(theme.hasIconRightPadding || 'inherit', "}\n\n[dir=rtl] .direction--down_3o4v .layout_3o4v,[dir=rtl] .direction--up_3o4v .layout_3o4v{padding-left:").concat(theme.hasIconRightPadding || 'inherit', "}\n\n.layout_3o4v{align-items:center;box-sizing:border-box;display:flex;height:100%;padding:").concat(theme.padding || 'inherit', ";width:100%}\n\n.newActivityButton_3o4v{offset-inline-start:0;position:absolute;top:100%}\n\n[dir=ltr] .newActivityButton_3o4v{left:0}\n\n[dir=rtl] .newActivityButton_3o4v{right:0}");
  },
  'root': 'root_3o4v',
  'icon': 'icon_3o4v',
  'direction--down': 'direction--down_3o4v',
  'layout': 'layout_3o4v',
  'direction--up': 'direction--up_3o4v',
  'newActivityButton': 'newActivityButton_3o4v'
};
import theme from './theme.js';
import ScreenReaderContent from '@instructure/ui-a11y/lib/components/ScreenReaderContent';

var StickyButton =
/*#__PURE__*/
function (_Component) {
  _inherits(StickyButton, _Component);

  function StickyButton() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, StickyButton);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(StickyButton)).call.apply(_getPrototypeOf2, [this].concat(args)));

    _this.handleClick = function (e) {
      var _this$props = _this.props,
          disabled = _this$props.disabled,
          onClick = _this$props.onClick;

      if (disabled) {
        e.preventDefault();
        e.stopPropagation();
      } else if (typeof onClick === 'function') {
        onClick(e);
      }
    };

    return _this;
  }

  _createClass(StickyButton, [{
    key: "renderIcon",
    value: function renderIcon() {
      var direction = this.props.direction;

      if (direction === 'up') {
        return React.createElement(IconArrowUpSolid, {
          className: styles.icon
        });
      } else if (direction === 'down') {
        return React.createElement(IconArrowDownLine, {
          className: styles.icon
        });
      } else {
        return null;
      }
    }
  }, {
    key: "renderDescription",
    value: function renderDescription() {
      if (this.props.description) {
        return React.createElement(ScreenReaderContent, {
          id: this.descriptionId
        }, this.props.description);
      }

      return null;
    }
  }, {
    key: "render",
    value: function render() {
      var _classes;

      var _this$props2 = this.props,
          id = _this$props2.id,
          children = _this$props2.children,
          disabled = _this$props2.disabled,
          hidden = _this$props2.hidden,
          direction = _this$props2.direction,
          zIndex = _this$props2.zIndex;
      var classes = (_classes = {}, _defineProperty(_classes, styles.root, true), _defineProperty(_classes, styles['direction--' + direction], direction !== 'none'), _classes);
      var style = {
        zIndex: zIndex ? zIndex : null
      };
      return React.createElement("span", null, React.createElement("button", {
        id: id,
        type: "button",
        onClick: this.handleClick,
        className: classnames(classes, styles.newActivityButton),
        style: style,
        "aria-disabled": disabled ? 'true' : null,
        "aria-hidden": hidden ? 'true' : null,
        ref: this.props.buttonRef,
        "aria-describedby": this.props.description ? this.descriptionId : null
      }, React.createElement("span", {
        className: styles.layout
      }, children, this.renderIcon())), this.renderDescription());
    }
  }, {
    key: "descriptionId",
    get: function get() {
      return "".concat(this.props.id, "_desc");
    }
  }]);

  StickyButton.displayName = "StickyButton";
  return StickyButton;
}(Component);

StickyButton.propTypes = {
  id: string.isRequired,
  children: node.isRequired,
  onClick: func,
  disabled: bool,
  hidden: bool,
  direction: oneOf(['none', 'up', 'down']),
  className: string,
  zIndex: number,
  buttonRef: func,
  description: string
};
StickyButton.defaultProps = {
  direction: 'none',
  offset: '0',
  className: ''
};
export default themeable(theme, styles)(StickyButton);